export interface InitialPlaybackGate {
    /** Queue playback until the initial presentation is visible, or start it immediately once ready. */
    requestPlayback(): void;
    /** Release at most one queued request after the initial preloader has been removed. */
    markPresentationReady(): void;
    /** Cancel playback that was requested while the preloader was still covering the scene. */
    cancelPendingPlayback(): void;
    /** Whether the initial preloader has been fully removed. */
    isPresentationReady(): boolean;
}

export function shouldRequestInitialPlayback(
    viewerAutoPlay?: boolean,
    configAutoPlay?: boolean,
    frameSequenceAutoPlay?: boolean,
): boolean {
    return viewerAutoPlay === true || configAutoPlay === true || frameSequenceAutoPlay === true;
}

/**
 * Coordinates playback requests with the initial scene presentation.
 *
 * The viewer can receive a play request from scene config, a URL parameter,
 * a ready-event listener, or the public API before its preloader has finished
 * fading out. Coalescing those requests behind this gate prevents the tour from
 * advancing invisibly and starting partway through once the scene is revealed.
 */
export function createInitialPlaybackGate(
    startPlayback: () => void,
    initiallyReady = false,
): InitialPlaybackGate {
    let presentationReady = initiallyReady;
    let playbackPending = false;

    return {
        requestPlayback(): void {
            if (presentationReady) {
                startPlayback();
                return;
            }
            playbackPending = true;
        },

        markPresentationReady(): void {
            if (presentationReady) return;
            presentationReady = true;
            if (!playbackPending) return;
            playbackPending = false;
            startPlayback();
        },

        cancelPendingPlayback(): void {
            playbackPending = false;
        },

        isPresentationReady(): boolean {
            return presentationReady;
        },
    };
}
